/**
 * `<HelpdeskRolesPage>` — `/w/:slug/helpdesk/roles`
 *
 * A `helpdesk_admin` authors custom roles scoped to the desk (helpdesk-team
 * unit). The editor's permission checklist is the backend-supplied `ceiling`
 * (HELPDESK_ROLE_CEILING) — a desk admin can only ever assemble roles WITHIN
 * the desk's own capabilities, never grant `member:*` / workspace admin. The
 * built-in helpdesk roles are shown read-only (they live in code).
 *
 * Gated at the route/nav on `manage-roles Helpdesk`.
 */

import { useAuth, useWorkspace } from "@cometchat-ai/auth/client";
import { flagEnabled } from "@cometchat-ai/auth/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorState } from "../components/error-state";
import { Loader2, Pencil, Plus, ShieldCheck, Trash2 } from "../components/icons";
import { PermissionCountPill } from "../components/permission-count-pill";
import { PermissionChecklist } from "../settings/permission-checklist";
import { FINAL_FONT } from "../layouts/final-polish";
import { PageShell } from "../components/page-shell";
import { SettingsCard } from "../components/settings-card";
import { formatApiError } from "../lib/format-error";
import { listWorkspaceWorkflows, resourcePermsKeys, type WorkflowPerm } from "../settings/api";
import { ResourceMatrix } from "../settings/resource-matrix";
import {
  createHelpdeskRole,
  deleteHelpdeskRole,
  getHelpdeskRoleWorkflowPerms,
  helpdeskRoleLabel,
  helpdeskTeamKeys,
  listHelpdeskRoles,
  setHelpdeskRoleWorkflowPerms,
  updateHelpdeskRole,
  type HelpdeskCustomRole,
} from "./team-api";

// Workflow ACTION verbs are authored in the dedicated All-vs-Specific section
// (below), not as generic ceiling checkboxes — so hide them from the
// checklist. The ceiling bounds these to run/signal/cancel: no publish/manage
// (the backend rejects those on a helpdesk role too). Stored queries are
// absent by design — the desk holds no query:* slugs.
const HD_WF_MATRIX = [
  { key: "run", slug: "workflow:run", label: "Run" },
  { key: "signal", slug: "workflow:signal", label: "Respond" },
  { key: "cancel", slug: "workflow:cancel", label: "Cancel" },
] as const;
const HD_HIDDEN_SLUGS = new Set<string>(HD_WF_MATRIX.map((v) => v.slug));

type Scope = "all" | "specific";
type WfCols = { run: boolean; signal: boolean; cancel: boolean };

const ROW_CLASS =
  "flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-[13px]";

export function HelpdeskRolesPage() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const qc = useQueryClient();

  const [editing, setEditing] = useState<HelpdeskCustomRole | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HelpdeskCustomRole | null>(null);

  const rolesQuery = useQuery({
    queryKey: helpdeskTeamKeys.roles(slug),
    queryFn: () => listHelpdeskRoles(slug),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteHelpdeskRole(slug, id),
    onSuccess: (res) => {
      toast.success(t("helpdeskTeam.roles.deletedToast", { count: res.repointed }));
      setDeleteTarget(null);
      void qc.invalidateQueries({ queryKey: helpdeskTeamKeys.roles(slug) });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const builtin = rolesQuery.data?.builtin ?? [];
  const custom = rolesQuery.data?.custom ?? [];
  const ceiling = rolesQuery.data?.ceiling ?? [];

  return (
    <PageShell
      className="p-0"
      data-testid="helpdesk-roles"
      description={t("helpdeskTeam.roles.subtitle")}
      actions={
        <Button
          onClick={() => setEditing("new")}
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          data-testid="helpdesk-role-new"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t("helpdeskTeam.roles.new")}
        </Button>
      }
    >
      <div className="mx-auto w-full max-w-3xl space-y-5 px-6 py-6">
        {rolesQuery.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner aria-label={t("helpdeskTeam.roles.builtinTitle")} />
          </div>
        ) : rolesQuery.error ? (
          <ErrorState message={rolesQuery.error.message} onRetry={rolesQuery.refetch} />
        ) : (
          <>
            <SettingsCard icon={ShieldCheck} title={t("helpdeskTeam.roles.builtinTitle")}>
              <ul className="space-y-2">
                {builtin.map((role) => (
                  <li key={role.name} className={ROW_CLASS}>
                    <span className="truncate font-medium text-foreground">
                      {helpdeskRoleLabel(role.name)}
                    </span>
                    <PermissionCountPill
                      label={t("helpdeskTeam.roles.permCount", { count: role.permissions.length })}
                      permissions={role.permissions}
                    />
                  </li>
                ))}
              </ul>
            </SettingsCard>

            <SettingsCard
              icon={ShieldCheck}
              title={
                custom.length === 1
                  ? t("helpdeskTeam.roles.customTitleOne")
                  : t("helpdeskTeam.roles.customTitleOther", { count: custom.length })
              }
            >
              {custom.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-muted-foreground">
                  {t("helpdeskTeam.roles.empty")}
                </p>
              ) : (
                <ul className="space-y-2">
                  {custom.map((role) => (
                    <li
                      key={role.id}
                      className={ROW_CLASS}
                      data-testid={`helpdesk-role-${role.id}`}
                    >
                      <span className="truncate font-medium text-foreground">{role.name}</span>
                      <div className="flex shrink-0 items-center gap-1">
                        <PermissionCountPill
                          label={t("helpdeskTeam.roles.permCount", {
                            count: role.permissions.length,
                          })}
                          permissions={role.permissions}
                          className="mr-1 inline-flex cursor-pointer items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        />
                        <button
                          type="button"
                          aria-label={t("helpdeskTeam.roles.edit")}
                          onClick={() => setEditing(role)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                          data-testid={`helpdesk-role-edit-${role.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label={t("helpdeskTeam.roles.delete")}
                          onClick={() => setDeleteTarget(role)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] hover:text-destructive"
                          data-testid={`helpdesk-role-delete-${role.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SettingsCard>
          </>
        )}
      </div>

      {editing ? (
        <HelpdeskRoleDialog
          slug={slug}
          role={editing === "new" ? null : editing}
          ceiling={ceiling}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: helpdeskTeamKeys.roles(slug) });
          }}
        />
      ) : null}

      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("helpdeskTeam.roles.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("helpdeskTeam.roles.deleteDescription", { name: deleteTarget?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t("helpdeskTeam.roles.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) remove.mutate(deleteTarget.id);
              }}
              disabled={remove.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {t("helpdeskTeam.roles.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function HelpdeskRoleDialog({
  slug,
  role,
  ceiling,
  onClose,
  onSaved,
}: {
  slug: string;
  role: HelpdeskCustomRole | null;
  ceiling: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation("workspace");
  const auth = useAuth();
  // Workflows surface is flag-gated (default OFF) — hide the workflow checklist
  // row + the Workflow access section until the infra ships. Hidden only; save
  // is untouched so a role that already holds workflow grants keeps them.
  const workflowsEnabled = flagEnabled(auth, "surface.workflows");
  const [name, setName] = useState(role?.name ?? "");
  // Ceiling checkboxes EXCLUDE the workflow/query verbs — those are authored in
  // the All-vs-Specific sections below.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((role?.permissions ?? []).filter((p) => !HD_HIDDEN_SLUGS.has(p))),
  );
  const [wfScope, setWfScope] = useState<Scope>(
    role && HD_WF_MATRIX.some((v) => role.permissions.includes(v.slug)) ? "all" : "specific",
  );
  const [wfBlanket, setWfBlanket] = useState<Set<string>>(
    () => new Set(HD_WF_MATRIX.filter((v) => role?.permissions.includes(v.slug)).map((v) => v.key)),
  );
  const [wfSpecific, setWfSpecific] = useState<Map<string, WfCols>>(new Map());
  const [seeded, setSeeded] = useState(false);

  const [wfListQ, wfPermQ] = useQueries({
    queries: [
      { queryKey: resourcePermsKeys.workflows(slug), queryFn: listWorkspaceWorkflows },
      {
        queryKey: helpdeskTeamKeys.roleWorkflow(slug, role?.id ?? "new"),
        queryFn: () => getHelpdeskRoleWorkflowPerms(slug, role!.id),
        enabled: !!role,
      },
    ],
  });
  const resourcesLoading = wfListQ.isPending;
  const permsSettled = !role || !wfPermQ.isPending;

  // Seed the per-resource matrix once the role's grants have loaded (edit).
  if (role && !seeded && permsSettled) {
    const wf = new Map<string, WfCols>();
    for (const g of wfPermQ.data ?? [])
      wf.set(g.workflowId, { run: g.run, signal: g.signal, cancel: g.cancel });
    setWfSpecific(wf);
    setSeeded(true);
  }

  // Group the ceiling slugs by subject (minus the matrix verbs) for the checklist.
  const groups = useMemo(() => {
    const bySubject = new Map<string, string[]>();
    for (const slug of ceiling) {
      if (HD_HIDDEN_SLUGS.has(slug)) continue;
      if (!workflowsEnabled && slug.startsWith("workflow:")) continue;
      const subject = slug.slice(0, slug.indexOf(":"));
      const arr = bySubject.get(subject);
      if (arr) arr.push(slug);
      else bySubject.set(subject, [slug]);
    }
    return [...bySubject.entries()];
  }, [ceiling, workflowsEnabled]);

  const toggle = (slug: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const save = useMutation({
    mutationFn: async () => {
      const permissions = [...selected];
      if (wfScope === "all")
        for (const v of HD_WF_MATRIX) if (wfBlanket.has(v.key)) permissions.push(v.slug);
      const body = { name: name.trim(), permissions };
      const saved = role
        ? await updateHelpdeskRole(slug, role.id, body)
        : await createHelpdeskRole(slug, body);
      const wfEntries: WorkflowPerm[] =
        wfScope === "specific"
          ? [...wfSpecific.entries()].map(([workflowId, f]) => ({
              workflowId,
              run: f.run,
              signal: f.signal,
              cancel: f.cancel,
              publish: false,
              manage: false,
            }))
          : [];
      await setHelpdeskRoleWorkflowPerms(slug, saved.id, wfEntries);
    },
    onSuccess: () => {
      toast.success(
        role ? t("helpdeskTeam.roles.updatedToast") : t("helpdeskTeam.roles.createdToast"),
      );
      onSaved();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const effectivePerms = selected.size + (wfScope === "all" ? wfBlanket.size : 0);
  const canSave = name.trim().length > 0 && effectivePerms > 0 && !save.isPending;

  const toggleBlanket = (set: Set<string>, setFn: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setFn(next);
  };
  const toggleMatrix = <C extends Record<string, boolean>>(
    map: Map<string, C>,
    setFn: (m: Map<string, C>) => void,
    id: string,
    col: string,
    empty: C,
  ) => {
    const next = new Map(map);
    const cur = next.get(id) ?? { ...empty };
    next.set(id, { ...cur, [col]: !cur[col] });
    setFn(next);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="helpdesk-role-dialog"
        style={{ fontFamily: FINAL_FONT }}
        className="flex max-h-[85vh] max-w-xl flex-col gap-0 rounded-2xl p-0 sm:rounded-2xl"
      >
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-semibold tracking-[0.01em]">
            {role ? t("helpdeskTeam.roles.editTitle") : t("helpdeskTeam.roles.newTitle")}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {t("helpdeskTeam.roles.dialogSubtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-2">
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium text-foreground">
              {t("helpdeskTeam.roles.nameLabel")}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("helpdeskTeam.roles.namePlaceholder")}
              data-testid="helpdesk-role-name"
              className="h-9 rounded-lg text-[13px]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-[13px] font-medium text-foreground">
              {t("helpdeskTeam.roles.permsLabel")}
            </Label>
            <PermissionChecklist
              groups={groups.map(([subject, permissions]) => ({ subject, permissions }))}
              selected={selected}
              onToggle={toggle}
              checkboxTestId={(slug) => `helpdesk-role-perm-${slug}`}
            />
          </div>

          {workflowsEnabled ? (
            <HdAccessSection
              title={t("roles.accessWorkflows")}
              allLabel={t("roles.accessAllWorkflows")}
              specificLabel={t("roles.accessSpecific")}
              emptyLabel={t("roles.accessNoWorkflows")}
              scope={wfScope}
              onScope={setWfScope}
              verbs={HD_WF_MATRIX}
              blanket={wfBlanket}
              onToggleBlanket={(k) => toggleBlanket(wfBlanket, setWfBlanket, k)}
              loading={resourcesLoading}
              rows={wfListQ.data ?? []}
              isChecked={(id, col) => Boolean(wfSpecific.get(id)?.[col as keyof WfCols])}
              onToggleSpecific={(id, col) =>
                toggleMatrix(wfSpecific, setWfSpecific, id, col, {
                  run: false,
                  signal: false,
                  cancel: false,
                })
              }
            />
          ) : null}
        </div>

        <DialogFooter className="flex-row gap-2 border-t border-border px-5 pb-5 pt-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={save.isPending}
            className="h-8 flex-1 rounded-md text-[13px] hover:bg-secondary-hover"
          >
            {t("helpdeskTeam.roles.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!canSave}
            data-testid="helpdesk-role-save"
            className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t("helpdeskTeam.roles.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A workflow/query access section for a helpdesk role: "All" (blanket verbs)
 * vs "Specific" (per-resource matrix). Verb columns are ceiling-bounded
 * (run/signal/cancel for workflows, run for queries) — no publish/manage/edit. */
function HdAccessSection({
  title,
  allLabel,
  specificLabel,
  emptyLabel,
  scope,
  onScope,
  verbs,
  blanket,
  onToggleBlanket,
  loading,
  rows,
  isChecked,
  onToggleSpecific,
}: {
  title: string;
  allLabel: string;
  specificLabel: string;
  emptyLabel: string;
  scope: Scope;
  onScope: (s: Scope) => void;
  verbs: ReadonlyArray<{ readonly key: string; readonly slug: string; readonly label: string }>;
  blanket: Set<string>;
  onToggleBlanket: (key: string) => void;
  loading: boolean;
  rows: Array<{ id: string; name: string }>;
  isChecked: (id: string, col: string) => boolean;
  onToggleSpecific: (id: string, col: string) => void;
}) {
  const seg = (value: Scope, label: string) => (
    <button
      type="button"
      onClick={() => onScope(value)}
      className={`h-7 rounded-md px-3 text-[12px] font-medium transition-colors ${
        scope === value
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-[13px] font-medium text-foreground">{title}</Label>
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5">
          {seg("all", allLabel)}
          {seg("specific", specificLabel)}
        </div>
      </div>
      {scope === "all" ? (
        <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-border px-4 py-3">
          {verbs.map((v) => (
            <label key={v.key} className="flex cursor-pointer items-center gap-2 text-[13px]">
              <Checkbox
                checked={blanket.has(v.key)}
                onCheckedChange={() => onToggleBlanket(v.key)}
                className="h-4 w-4 rounded-[5px]"
              />
              <span className="text-foreground">{v.label}</span>
            </label>
          ))}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-6">
          <Spinner aria-label={title} />
        </div>
      ) : (
        <ResourceMatrix
          rows={rows}
          cols={verbs.map((v) => ({ key: v.key, label: v.label }))}
          emptyLabel={emptyLabel}
          isChecked={isChecked}
          onToggle={onToggleSpecific}
        />
      )}
    </div>
  );
}
